import { OfferResponse } from 'states/initialState';

/* istanbul ignore next */ 
const sortBy = <T extends Record<string, any>, K extends keyof T>(arr: T[], k: K): T[] => (arr.concat().sort((a, b) => (a[k] > b[k] ? 1 : a[k] < b[k] ? -1 : 0)));
const pick = (objs, keys) => objs.map((obj) => Object.keys(obj).filter((k) => keys.includes(k)).reduce((res, k) => Object.assign(res, { [k]: obj[k] }), {}));
const pluck = (objs:object[], property:string):any[] => objs.map((obj) => obj[property]);
const roundAttributes = (obj, roundTo, atts) => { atts.map((att) => { obj[att] = (Math.round(Number(obj[att]) * Math.pow(10, roundTo)) / Math.pow(10, roundTo)) }); return obj }
const omit = (obj, keys) => Object.keys(obj).filter((k) => !keys.includes(k)).reduce((res, k) => Object.assign(res, { [k]: obj[k] }), {});

interface Response {
  ok: boolean;
  status: number;
  statusText?: string;
}

const parseRes = (res: any, response: Response) => {
  const { ok, status, statusText } = response;
  if (!ok) {
    return { status, message: statusText };
  }
  return res;
};

interface GetObject {
  get: Function;
  response: Response;
}

interface PostObject {
  post: Function;
  response: Response;
}

export const getDeal = async ({ get, response }: GetObject) => {
  const res: any = await get('/deal');
  return parseRes(res, response);
};

export const getOfferDetails = async (
  { get, response }: GetObject,
  id: string
) => {
  const res: any = await get(`/partner/application/${id}/offer`);
  const fullResult = parseRes(res, response);
  if (fullResult.status) { return fullResult }

  const lineOfCredit = fullResult.products.filter(p => p.type === 'LOC')[0]?.offers[0];
  const customerDetails = omit(fullResult.customerDetails, 'accountManagerDetails');
  const rawTerms = fullResult.products.filter(p => p.type === 'TermLoan')[0]?.offers;
  const reducedTerms = rawTerms && pick(rawTerms, [
    'offerId',
    'buyRate',
    'sellRate', 
    'term',
    'lineAmount',
    'centsOnDollar',
    'paymentFrequency',
    'prepaymentDiscountPercentage'
  ]);
  const roundedTerms = reducedTerms && reducedTerms?.map((term) => roundAttributes(term, 3, ['buyRate', 'sellRate', 'centsOnDollar']));
  const sortedTerms = roundedTerms && sortBy(sortBy(roundedTerms, 'centsOnDollar'), 'term');
  const maxLineAmount = rawTerms && Math.max(...pluck(rawTerms, 'lineAmount'))

  return {
    applicationType: fullResult.applicationType,
    accountManagerDetails: fullResult.customerDetails.accountManagerDetails,
    partnerDetails: fullResult.partnerDetails,
    lineOfCreditOffer: lineOfCredit,
    selectedOffer: fullResult.selectedOffer,
    offer: {
      maxLineAmount: maxLineAmount,
      terms: sortedTerms
    },
    customerData: {
      partner: fullResult.partnerDetails.partner,
      partnerId: fullResult.partnerDetails.partnerId,
      ...customerDetails
    }
  };
};

export const postOffer = async (
  { post, response }: PostObject,
  offer: OfferResponse
) => {
  const res: any = await post('/pricing', offer);
  if (response.ok) return parseRes(res, response);
  else return response;
};

export const postOfferConfirm = async (
  { post, response }: PostObject,
  businessId: string,
  confirmRequest: OfferResponse
) => {
  const res: any = await post(
    `/partner/application/${businessId}/offer/confirm`,
    confirmRequest
  );
  if (response.ok) return parseRes(res, response);
  else return response;
};

export const uploadFile = async (
  { post, response }: PostObject,
  businessId: string,
  files: File[]
) => {
  let formData = new FormData();
  files.forEach(file => {
    formData.append('file', file);
  });

  const res: any = await post(
    `partner/application/${businessId}/closing-documents`,
    formData
  );
  if (response.ok) return parseRes(res, response);
  else return response;
};

export const getRequiredFiles = async (
  { get, response }: GetObject,
  businessId: string
) => {
  const res: any = await get(
    `partner/application/${businessId}/closing-documents`
  );
  if (response.ok) return parseRes(res, response);
  else return response;
};
